import { useEffect } from 'react';

import { Controller, SubmitHandler, useForm } from 'react-hook-form';

import { CreateUserRequestType } from 'api'; // Assuming admins might use a similar request type or will need a new one e.g., CreateAdminRequestType
import { CommonModalType, ReturnComponentType } from 'common';
import { CommonModal } from 'components/Modals/CommonModal';
import {
  commonValidation,
  confirmPasswordValidation,
  createPasswordValidation,
  emailValidation,
  updatePasswordValidation,
} from 'config';
import { CommonCharacters, Roles } from 'enums'; // Roles.ADMIN or similar will be used
import {
  useAppSelector,
  // useSelectCommunities, // Removed, assuming not needed for admins
  // useSelectProperties,  // Removed, assuming not needed for admins
  useTypedDispatch,
} from 'hooks';
import {
  createUser,
  editUser,
  fetchCertainCompany,
  fetchCertainUser,
  selectCertainUser,
} from 'state';
// import { setCertainUser } from 'state/actions'; // To be replaced with setCertainAdmin
import { Button, InputForm } from 'ui-kit';
// import { ISelectOption } from 'ui-kit/Select/Select'; // Removed, assuming no role select like for customers
import {
  // checkIsCustomer, // Removed
  checkIsGoodStatus,
  chooseAvatarFromRole, // May need adjustment for admin roles
  chooseTitleFromRole, // May need adjustment for admin roles
} from 'utils';
import './ModalAddAdmin.scss';

type ModalAddAdminProps = CommonModalType & {
  adminId?: string;
  companyId: string;
};

export const ModalAddAdmin = ({
  showModal,
  setShowModal,
  adminId,
  companyId,
}: ModalAddAdminProps): ReturnComponentType => {
  const dispatch = useTypedDispatch();
  const user = useAppSelector(selectCertainUser); // Using placeholder selector

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm<Omit<CreateUserRequestType, 'role' | 'communityIds' | 'propertyIds'>>({
    // Adjusted type, removing fields not relevant for a simple admin
    defaultValues: {
      email: '',
      password: '',
      passwordConfirm: '',
      firstName: '',
      lastName: '',
      companyId,
    },
  });

  useEffect(() => {
    if (adminId) {
      if (user) {
        reset({
          email: user.email ?? '',
          password: adminId ? CommonCharacters.HIDDEN_PASSWORD : '', // Pre-fill for edit, but don't show actual password
          passwordConfirm: '',
          firstName: user.firstName ?? '',
          lastName: user.lastName ?? '',
        });
      } else {
        dispatch(fetchCertainUser(adminId));
      }
    } else if (!adminId) {
      reset({
        email: '',
        password: '',
        passwordConfirm: '',
        firstName: '',
        lastName: '',
      });
    }
  }, [adminId, dispatch, reset, user]);

  const closeAddAdminModal = () => {
    setShowModal(false);
    reset(); // Reset form to default values
  };

  const onSubmit: SubmitHandler<
    Omit<CreateUserRequestType, 'role' | 'communityIds' | 'propertyIds'>
  > = async data => {
    const adminPayload = {
      ...data,
      role: Roles.COMPANY_ADMIN,
      communityIds: [],
      propertiesIds: [],
      companyId,
    };
    let action;

    if (adminId) {
      if (data.password !== CommonCharacters.HIDDEN_PASSWORD) {
        adminPayload.password = data.password;
      }
      action = editUser({ ...adminPayload, id: adminId });
    } else {
      adminPayload.password = adminPayload.password || '';
      adminPayload.passwordConfirm = adminPayload.password;
      action = createUser(adminPayload);
    }

    const status = await dispatch(action);

    if (checkIsGoodStatus(status)) {
      dispatch(fetchCertainCompany(companyId));
      closeAddAdminModal();
    }
  };

  // Simplified avatar and title logic, assuming a generic admin role
  const adminRoleForDisplay = adminId && user ? user.role : Roles.COMPANY_ADMIN;
  const modalTitle = adminId ? 'Edit Admin' : 'Add New Admin';

  return (
    <CommonModal closeModal={closeAddAdminModal} showModal={showModal}>
      <div className="ModalAddAdmin">
        <header className="ModalAddAdmin_Header">
          <h3>{modalTitle}</h3>
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
          <div onClick={closeAddAdminModal} className="ModalAddAdmin_Header_Close">
            &#10006;
          </div>
        </header>

        <form className="ModalAddAdmin_Form" onSubmit={handleSubmit(onSubmit)}>
          <section style={{ alignItems: 'center' }}>
            <img
              src={chooseAvatarFromRole(adminRoleForDisplay)} // Adjust if admin avatars are different
              alt="admin-avatar"
            />
            <h4>{chooseTitleFromRole(adminRoleForDisplay)}</h4>{' '}
            {/* Adjust if admin titles are different */}
          </section>

          <section className="row">
            <Controller
              control={control}
              name="firstName"
              rules={commonValidation}
              render={({ field }) => (
                <InputForm
                  label="First name"
                  className="ModalAddAdmin_Form_Input"
                  value={field.value}
                  onChange={e => field.onChange(e)}
                  error={errors.firstName?.message}
                  autoFocus
                />
              )}
            />
            <Controller
              control={control}
              name="lastName"
              rules={commonValidation}
              render={({ field }) => (
                <InputForm
                  label="Last name"
                  className="ModalAddAdmin_Form_Input"
                  value={field.value}
                  onChange={e => field.onChange(e)}
                  error={errors.lastName?.message}
                />
              )}
            />
          </section>

          <section>
            <Controller
              control={control}
              name="email"
              rules={emailValidation}
              render={({ field }) => (
                <InputForm
                  label="Email"
                  className="ModalAddAdmin_Form_Input"
                  type="email"
                  value={field.value}
                  onChange={e => field.onChange(e)}
                  error={errors.email?.message}
                />
              )}
            />
          </section>
          <section>
            <Controller
              control={control}
              name="password"
              rules={adminId ? updatePasswordValidation : createPasswordValidation}
              render={({ field }) => (
                <InputForm
                  label={adminId ? 'New password (optional)' : 'Password'}
                  className="ModalAddAdmin_Form_Input"
                  type="password"
                  value={field.value}
                  onChange={e => field.onChange(e)}
                  error={errors.password?.message}
                  placeholder={adminId ? 'Leave blank to keep current password' : ''}
                />
              )}
            />
            <Controller
              control={control}
              name="passwordConfirm"
              rules={confirmPasswordValidation}
              render={({ field }) => (
                <InputForm
                  label="Confirm password"
                  className="ModalAddAdmin_Form_Input"
                  type="password"
                  value={field.value}
                  onChange={e => field.onChange(e)}
                  error={errors.passwordConfirm?.message}
                />
              )}
            />
          </section>
          <Button type="submit" className="ModalAddAdmin_Form_Submit">
            {adminId ? 'Save Changes' : 'Save'}
          </Button>
        </form>
      </div>
    </CommonModal>
  );
};
